import { acquireToken } from './utils/acquireToken';
import { createMsGraphClient } from './utils/msGraphClient';
import { microsoftGraphAPI } from './utils/msGraphAPI';
import { determineActions, createBatchRequests } from './utils/actionHandler';
import { enqueueLoadingSnackbar, closeLoadingSnackbar } from './utils/notifications';
import {
  dateTimeFormat,
  enqueueErrorSnackbar,
  enqueueSuccessSnackbar,
  formatDate,
  serializeError
} from 'helpers/AppHelpers';
import { captureException, withScope } from '@sentry/react';

export const useMSCalendarIntegration = ({ instance, accounts, localEvents }) => {
  const handleSync = async () => {
    let token;
    try {
      token = await acquireToken(instance, accounts);
    } catch (err) {
      console.log(
        `Step: Acquire Token - Failed to get access token. Reason: ${err.message || err}`
      );
    }

    if (!token || token.trim() === '') return;

    enqueueLoadingSnackbar('The synchronization of your events is currently in progress...');

    try {
      const msGraphClient = createMsGraphClient(token);
      const { getCalendarId, getMicrosoftEvents, sendEventsInBatch } =
        microsoftGraphAPI(msGraphClient);

      let calendarId;
      try {
        calendarId = await getCalendarId();

        if (!calendarId) {
          throw new Error('Step: Get Calendar ID - Received invalid calendar ID.');
        }
      } catch (err) {
        throw new Error(
          `Step: Get Calendar ID - Unable to retrieve calendar ID. Reason: ${err.message || err}`
        );
      }

      let msEvents;
      try {
        const result = await getMicrosoftEvents(calendarId);

        msEvents = result?.events;

        if (!Array.isArray(msEvents)) {
          throw new Error(
            'Step: Fetch Events - Expected an array of events but received invalid data.'
          );
        }
      } catch (err) {
        throw new Error(
          `Step: Fetch Events - Could not fetch events from Microsoft Calendar. Reason: ${
            err.message || err
          }`
        );
      }

      let actions;
      try {
        actions = determineActions(localEvents, msEvents);
      } catch (err) {
        throw new Error(
          `Step: Determine Actions - Failed to calculate sync actions from local and remote events. Reason: ${
            err.message || err
          }`
        );
      }

      let requests;
      try {
        requests = createBatchRequests(calendarId, actions);
      } catch (err) {
        throw new Error(
          `Step: Create Batch Requests - Error while building batch requests for calendar synchronization. Reason: ${
            err.message || err
          }`
        );
      }

      try {
        await sendEventsInBatch(requests);
      } catch (err) {
        throw new Error(
          `Step: Send Events - Failed to send batch update requests to Microsoft Calendar. Reason: ${
            err.message || err
          }`
        );
      }

      enqueueSuccessSnackbar(
        'Synchronization completed successfully. All events have been successfully synchronized with your Microsoft calendar.'
      );
    } catch (error) {
      console.error(`Calendar synchronization failed. Details: ${error.message || error}`);

      enqueueErrorSnackbar(
        'An error occurred while syncing your events. Please try again. If the issue persists, contact support for assistance.'
      );

      error.name = 'Sync MS Calendar Error';

      withScope((scope) => {
        scope.setExtra('error', serializeError(error));
        scope.setExtra('time', formatDate(new Date(), dateTimeFormat));
        scope.setExtra('sync_calendar', true);
        captureException(error);
      });
    }
    closeLoadingSnackbar();
  };

  return { handleSync };
};
